import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  ChevronDown, 
  Target, 
  Zap, 
  Mail, 
  Check, 
  Star,
  Shield,
  Clock,
  Users,
  X as XIcon,
  ArrowRight,
  FileText,
  Key,
  Rocket,
  MessageSquare,
  Send,
  CheckCircle2,
  Circle,
  ChevronLeft,
  ChevronRight,
  FileImage,
  Calendar,
  Loader2,
  Linkedin,
  ShieldCheck
} from 'lucide-react';
import { 
  heroStats, 
  pricingTiers, 
  faqItems,
  howItWorksSteps,
  dashboardPreview,
  monthlyResults,
  comparisonData
} from '../data/mock';
import { captureAdTracking } from '../utils/tracking';
import './LandingPage.css';
import SplitPaymentTimeline from '../components/SplitPaymentTimeline';
import InlineLeadForm from '../components/InlineLeadForm';

// Smooth scroll to the hero lead-capture form. Used by every CTA on the page
// so a click never opens a modal, it just brings the visible form into view
// and focuses the first field. Also fires a lightweight GA event.
const scrollToLeadForm = (source) => {
  const el = document.getElementById('lead-form');
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const targetY = window.scrollY + rect.top - 80;
  window.scrollTo({ top: targetY, behavior: 'smooth' });
  // Focus first empty input for extra conversion nudge
  setTimeout(() => {
    const nameInput = document.querySelector('[data-testid="inline-lead-form-hero"] [data-testid="ilf-input-name"]');
    if (nameInput) nameInput.focus({ preventScroll: true });
  }, 550);
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('event', 'cta_scroll_to_form', {
        event_category: 'engagement',
        event_label: source || 'unknown',
      });
    } catch (e) { /* noop */ }
  }
};

// Scroll reveal hook
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated counter hook
const useAnimatedCounter = (end, duration = 2000, isVisible) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration, isVisible]);
  
  return count;
};

// Urgency Bar — slim top strip, offer + scarcity, scrolls to form
const UrgencyBar = () => {
  return (
    <div className="urgency-bar" data-testid="urgency-bar">
      <span className="urgency-text">
        🔥 Limited seats this month · 10+ interviews guaranteed in 90 days
      </span>
      <button
        type="button"
        className="urgency-cta"
        onClick={() => scrollToLeadForm('urgency_bar')}
        data-testid="urgency-bar-cta"
      >
        Get My Free Plan →
      </button>
    </div>
  );
};

// Header Component. Meta Ads optimized: no exit links, only logo + CTA
const Header = ({ showSticky }) => {
  return (
    <header className={`header ${showSticky ? 'header-sticky' : ''}`} data-testid="site-header">
      <div className="header-container">
        <a href="#" className="header-logo">
          <img src="/logo.webp" alt="BulkJobApply - Job Search Outsourcing Service" className="logo-image" width="140" height="30" />
        </a>

        <div className="header-cta">
          <button
            type="button"
            className="cta-button-header header-reserve-btn"
            onClick={() => scrollToLeadForm('header')}
            data-testid="header-reserve-btn"
          >
            Get My Free Plan
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};

// Hero Section. 2-column: pitch (left) + inline lead form (right)
const HeroSection = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section className="hero-section" ref={ref} aria-label="Hero - Outsource your job search">
      <div className={`hero-container hero-split ${isVisible ? 'animate-fade-up' : ''}`}>
        <div className="hero-content hero-content-left">
          <span className="hero-eyebrow" data-testid="hero-eyebrow">
            <span className="hero-eyebrow-dot" />
            Trusted by 800+ engineers, PMs & data folks
          </span>

          <h1 className="hero-headline" data-testid="hero-headline">
            Get{" "}
            <span className="hero-mark" data-testid="hero-mark">
              <span className="hero-mark-phrase">
                Hired
                <svg className="hero-curve" viewBox="0 0 200 14" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M3 9 Q 50 2, 100 7 T 197 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </span>
            </span>{" "}
            in{" "}
            <span className="hero-mark">
              <span className="hero-mark-phrase">
                90 Days
                <svg className="hero-curve" viewBox="0 0 280 14" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M3 8 Q 70 2, 140 7 T 277 5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </span>
            </span>.
          </h1>

          <p className="hero-tagline" data-testid="hero-tagline">
            Your personal team hand-applies to <strong>780+ roles</strong> a month and triple-touches every hiring manager: <strong>application + LinkedIn DM + cold email</strong>. <strong>Guaranteed 10+ interviews in 90 days</strong>, or we keep working free until you get them.
          </p>

          <ul className="hero-bullets" data-testid="hero-bullets">
            <li>
              <span className="hero-bullet-check" aria-hidden="true">✓</span>
              <span><strong>Winner resume rebuilt</strong> by a real HR specialist</span>
            </li>
            <li>
              <span className="hero-bullet-check" aria-hidden="true">✓</span>
              <span><strong>Your own WhatsApp group</strong> with the team from day 1</span>
            </li>
            <li>
              <span className="hero-bullet-check" aria-hidden="true">✓</span>
              <span><strong>Live dashboard</strong> tracks every application</span>
            </li>
          </ul>

          <div className="hero-stats" data-testid="hero-stats">
            {heroStats.map((stat, index) => (
              <div key={index} className="stat-item">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-form-col">
          <InlineLeadForm
            source="hero"
            variant="hero"
            ctaText="Get My Free Plan"
          />

          <div className="hero-team" data-testid="hero-team">
            <span className="hero-team-label">Your personal team:</span>
            <div className="hero-team-chips">
              <div className="hero-team-chip">
                <img src="https://plus.unsplash.com/premium_photo-1682089810582-f7b200217b67?w=50&q=80&auto=format&fit=crop" alt="Priya, HR Specialist" width="28" height="28" loading="lazy" />
                <span>Priya · HR Specialist</span>
              </div>
              <div className="hero-team-chip">
                <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50&q=80&auto=format&fit=crop" alt="Sarah, Job Assistant" width="28" height="28" loading="lazy" />
                <span>Sarah · Job Assistant</span>
              </div>
              <div className="hero-team-chip">
                <img src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop" alt="Account Manager" width="28" height="28" loading="lazy" />
                <span>+ Account Manager</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Guarantee Strip, risk reversal, right under the hero
const GuaranteeStrip = () => {
  return (
    <section className="guarantee-strip" data-testid="guarantee-strip">
      <div className="guarantee-strip-inner">
        <div className="guarantee-icon-wrap">
          <ShieldCheck className="w-8 h-8" />
        </div>
        <div className="guarantee-text">
          <h2 className="guarantee-title">
            Guaranteed: <span className="guarantee-highlight">10+ Interviews in 90 Days</span>
          </h2>
          <p className="guarantee-sub">
            That's the minimum, most clients get far more. If we fall short, we keep working free until you get them. And if 10 interviews don't turn into an offer, your winning resume and the Triple-Touch automation stay yours to keep going.
          </p>
        </div>
      </div>
    </section>
  );
};

// Triple-Touch System, the named differentiator: every job gets 3 touches
const TripleTouchSection = () => {
  const [ref, isVisible] = useScrollReveal();
  const touches = [
    {
      icon: FileText,
      step: "Touch 1",
      title: "The Tailored Application",
      desc: "A human hand-crafts your resume and cover letter for that exact role and submits it personally. No AI slop, no copy-paste templates."
    },
    {
      icon: Linkedin,
      step: "Touch 2",
      title: "The LinkedIn DM",
      desc: "We find the hiring manager behind that exact job post and send them a personal message about you, straight into their inbox, not the ATS pile."
    },
    {
      icon: Mail,
      step: "Touch 3",
      title: "The Cold Email",
      desc: "The HR team gets a short, sharp email pitching you directly. Three doors knocked for every single job, while others knock once."
    }
  ];

  return (
    <section className="triple-touch-section" id="triple-touch" ref={ref} data-testid="triple-touch-section">
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">The Triple-Touch System™</span>
        <h2 className="section-headline">
          It's not luck. It's 3 touches on every single job.
        </h2>
        <p className="section-subtitle">
          Anyone can spray applications. We make sure a real human hears about you three different ways, for every role we target.
        </p>

        <div className="triple-touch-grid">
          {touches.map((t, index) => {
            const IconComponent = t.icon;
            return (
              <div
                key={t.step}
                className={`triple-touch-card ${isVisible ? 'animate-stagger' : ''}`}
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`touch-card-${index + 1}`}
              >
                <span className="touch-step">{t.step}</span>
                <div className="touch-icon">
                  <IconComponent className="w-7 h-7" />
                </div>
                <h3 className="touch-title">{t.title}</h3>
                <p className="touch-desc">{t.desc}</p>
              </div>
            );
          })}
        </div>

        <p className="triple-touch-closer">
          Every job gets all three. That's why our clients get calls while others get silence.
        </p>

        <button
          type="button"
          className="cta-button-large"
          onClick={() => scrollToLeadForm('triple_touch')}
          data-testid="triple-touch-cta"
        >
          Get My Free Plan
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
};

// Company Logos Section
const LogosSection = () => {
  const companies = [
    'Google', 'Amazon', 'Meta', 'Microsoft', 'Deloitte', 
    'Philips', 'Netflix', 'Apple', 'Tesla', 'Adobe'
  ];

  return (
    <section className="logos-section">
      <p className="logos-label">Trusted by professionals who landed jobs at</p>
      <div className="logos-scroll">
        <div className="logos-track">
          {[...companies, ...companies].map((company, index) => (
            <span key={index} className="platform-logo">{company}</span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution + How It Works Section (combined)
const SolutionHowItWorksSection = () => {
  const [ref, isVisible] = useScrollReveal();
  const iconMap = { Target, Zap, Mail, Users, FileText, Key, Rocket };

  return (
    <section className="solution-section" id="how-it-works" ref={ref}>
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">How It Works</span>
        <h2 className="section-headline">
          From Signup to Interview Calls: 14 Days on Average
        </h2>
        <p className="section-subtitle">
          Three steps. Zero effort from you. Here's exactly what happens after you fill the form.
        </p>

        <div className="how-it-works-inline">
          <div className="timeline-container">
            {howItWorksSteps.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              // Team member images: Step 2 = Priya (HR Specialist), Step 3 = Sarah (Dedicated Assistant)
              const teamImages = [
                null,
                "https://plus.unsplash.com/premium_photo-1682089810582-f7b200217b67?w=100&q=80&auto=format&fit=crop",
                "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80&auto=format&fit=crop",
              ];
              const teamNames = [null, "Priya · HR Specialist", "Sarah · Your Assistant"];
              
              return (
                <div 
                  key={step.step} 
                  className={`timeline-item ${isVisible ? 'animate-stagger' : ''}`}
                  style={{ animationDelay: `${(index + 3) * 150}ms` }}
                >
                  <div className="timeline-marker">
                    <div className="timeline-icon">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    {index < howItWorksSteps.length - 1 && <div className="timeline-line"></div>}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-duration">{step.duration}</div>
                    <h4 className="timeline-title">{step.title}</h4>
                    <p className="timeline-description">{step.description}</p>
                    {teamImages[index] && (
                      <div className="timeline-team-member">
                        <img src={teamImages[index]} alt={teamNames[index]} className="team-member-image" />
                        <span className="team-member-name">{teamNames[index]}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="whatsapp-preview">
            <div className="whatsapp-header">
              <div className="whatsapp-avatar">BJ</div>
              <div className="whatsapp-info">
                <span className="whatsapp-name">BulkJobApply Team</span>
                <span className="whatsapp-members">HR, Manager, Assistant, You</span>
              </div>
            </div>
            <div className="whatsapp-messages">
              <div className="message incoming">
                <img src="https://plus.unsplash.com/premium_photo-1682089810582-f7b200217b67?w=50&q=80&auto=format&fit=crop" alt="Priya" className="message-avatar" />
                <div className="message-content">
                  <span className="message-sender">Priya · HR Specialist</span>
                  <p>Hi! I'm Priya, your HR specialist. I'll have your ATS-optimized resume ready in the next 48 hours, rebuilt from scratch for your target roles.</p>
                </div>
              </div>
              <div className="message incoming">
                <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50&q=80&auto=format&fit=crop" alt="Sarah" className="message-avatar" />
                <div className="message-content">
                  <span className="message-sender">Sarah · Your Dedicated Assistant</span>
                  <p>Hey! I'm Sarah. Starting Day 3, I'm hand-applying to 780 jobs a month on career pages. Tailored resumes, real humans, no AI slop.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


// Comparison Section
const ComparisonSection = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section className="comparison-section" id="comparison" ref={ref}>
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">Why Choose Us</span>
        <h2 className="section-headline">
          The Only Service That Triple-Touches Every Hiring Manager
        </h2>
        <p className="section-subtitle">
          Spray-and-pray tools just apply. We make sure a real human notices you, three ways, every time.
        </p>

        <p className="scroll-hint">Scroll right to see all options →</p>
        <div className="comparison-table-wrapper">
          <table className="comparison-table" data-testid="comparison-table">
            <thead>
              <tr>
                {comparisonData.headers.map((header, i) => (
                  <th key={i} className={i === 0 ? 'feature-column' : i === comparisonData.headers.length - 1 ? 'option-column highlight-column' : 'option-column'}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonData.rows.map((row, index) => (
                <tr key={index} className={isVisible ? 'animate-stagger' : ''} style={{ animationDelay: `${index * 50}ms` }}>
                  <td className="feature-cell">{row.feature}</td>
                  {row.values.map((val, i) => (
                    <td key={i} className={`check-cell ${row.highlight[i] ? 'highlight-cell' : ''}`}>
                      <span className={row.highlight[i] ? 'cell-value-highlight' : 'cell-value'}>{val}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="summary-row">
                <td className="feature-cell summary-label">Bottom Line</td>
                {comparisonData.bottomLine.map((item, i) => {
                  const colorClass = i === comparisonData.bottomLine.length - 1 ? 'good' : ['bad-burn', 'bad-hype', 'bad-pay'][i];
                  return (
                    <td key={i} className={`summary-cell ${colorClass}`}>
                      <span>{item.label}</span>
                      <span className="emoji">{item.emoji}</span>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  );
};

// Dashboard Demo Section
const DashboardDemo = () => {
  const [ref, isVisible] = useScrollReveal();
  const [activeTab, setActiveTab] = useState('activity');
  const animatedApps = useAnimatedCounter(dashboardPreview.totalApplications, 2000, isVisible);
  const animatedResponses = useAnimatedCounter(dashboardPreview.responses, 1500, isVisible);
  const animatedInterviews = useAnimatedCounter(dashboardPreview.interviews, 1500, isVisible);

  return (
    <section className="dashboard-demo-section" id="demo" ref={ref}>
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">Live Demo</span>
        <h2 className="section-headline">
          Your Personal Command Center
        </h2>
        <p className="section-subtitle">
          Track every application, response, and interview in real time with our dashboard.
        </p>

        <div className="dashboard-container">
          <div className="dashboard-header">
            <div className="dashboard-tabs">
              <button 
                className={`dash-tab ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                Live Activity
              </button>
              <button 
                className={`dash-tab ${activeTab === 'stats' ? 'active' : ''}`}
                onClick={() => setActiveTab('stats')}
              >
                Stats
              </button>
              <button 
                className={`dash-tab ${activeTab === 'tools' ? 'active' : ''}`}
                onClick={() => setActiveTab('tools')}
              >
                Tools
              </button>
            </div>
            <div className="dashboard-user">
              <div className="user-avatar">JD</div>
              <span>John Doe</span>
            </div>
          </div>

          <div className="dashboard-content">
            {activeTab === 'stats' && (
              <div className="dashboard-overview">
                <div className="stat-cards">
                  <div className="dash-stat-card">
                    <div className="dash-stat-icon">
                      <Send className="w-5 h-5" />
                    </div>
                    <div className="dash-stat-info">
                      <span className="dash-stat-value">{animatedApps.toLocaleString()}</span>
                      <span className="dash-stat-label">Total Applications</span>
                    </div>
                  </div>
                  
                  <div className="dash-stat-card">
                    <div className="dash-stat-icon">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <div className="dash-stat-info">
                      <span className="dash-stat-value">{animatedResponses}</span>
                      <span className="dash-stat-label">Responses</span>
                    </div>
                  </div>
                  
                  <div className="dash-stat-card highlight">
                    <div className="dash-stat-icon">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div className="dash-stat-info">
                      <span className="dash-stat-value">{animatedInterviews}</span>
                      <span className="dash-stat-label">Interviews</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="dashboard-activity">
                <div className="activity-feed">
                  {dashboardPreview.recentActivity.map((item, index) => (
                    <div key={index} className="activity-item" style={{ animationDelay: `${index * 100}ms` }}>
                      <div className={`activity-status ${item.status.toLowerCase()}`}>
                        {item.status === 'Applied' && <Circle className="w-3 h-3" />}
                        {item.status === 'Response' && <MessageSquare className="w-3 h-3" />}
                        {item.status === 'Interview' && <CheckCircle2 className="w-3 h-3" />}
                      </div>
                      <div className="activity-info">
                        <span className="activity-company">{item.company}</span>
                        <span className="activity-role">{item.role}</span>
                      </div>
                      <div className="activity-meta">
                        <span className={`activity-badge ${item.status.toLowerCase()}`}>{item.status}</span>
                        <span className="activity-time">{item.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="live-indicator">
                  <span className="pulse-dot"></span>
                  Applications going out in real time
                </div>
              </div>
            )}

            {activeTab === 'tools' && (
              <div className="dashboard-tools">
                <div className="tool-card">
                  <div className="tool-icon chill">
                    <Linkedin className="w-6 h-6" />
                  </div>
                  <div className="tool-info">
                    <h4>LinkedIn DM</h4>
                    <p>Hiring manager outreach</p>
                  </div>
                  <div className="tool-status active">
                    <span className="pulse-dot"></span>
                    Running
                  </div>
                </div>
                
                <div className="tool-card">
                  <div className="tool-icon refer">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="tool-info">
                    <h4>Cold Email</h4>
                    <p>Direct HR pitching</p>
                  </div>
                  <div className="tool-status active">
                    <span className="pulse-dot"></span>
                    Active
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="platforms-strip">
          <span className="platforms-label">Direct applications to company career pages</span>
          <div className="platforms-logos">
            <span className="platform-logo">Workday</span>
            <span className="platform-logo">Greenhouse</span>
            <span className="platform-logo">Lever</span>
            <span className="platform-logo">iCIMS</span>
            <span className="platform-logo">SuccessFactors</span>
          </div>
        </div>
      </div>
    </section>
  );
};


// Testimonials Section with Real WhatsApp Screenshots
const TestimonialsSection = () => {
  const [ref, isVisible] = useScrollReveal();
  const [expandedImage, setExpandedImage] = useState(null);
  
  const testimonialImages = Array.from({ length: 32 }, (_, i) => ({
    id: i + 2,
    image: `/testimonials_new/page_${i + 2}.webp`
  }));

  const firstRow = testimonialImages.slice(0, 16);
  const secondRow = testimonialImages.slice(16, 32);

  return (
    <section className="testimonials-section" id="testimonials" ref={ref}>
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">Straight from Our DMs</span>
        <h2 className="section-headline">Real Results from Real People</h2>
        <p className="section-subtitle">
          Actual WhatsApp messages from clients landing interviews and offers at top companies. Hover to pause, click to expand.
        </p>

        <div className="testimonials-marquee-wrapper" data-testid="testimonials-marquee">
          <div className="testimonials-marquee-track row-1">
            {[...firstRow, ...firstRow].map((item, index) => (
              <div 
                key={`r1-${item.id}-${index}`} 
                className="testimonial-marquee-card"
                onClick={() => setExpandedImage(item)}
              >
                <img 
                  src={item.image} 
                  alt={`Client testimonial ${item.id}`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="testimonials-marquee-wrapper" data-testid="testimonials-marquee-2">
          <div className="testimonials-marquee-track row-2">
            {[...secondRow, ...secondRow].map((item, index) => (
              <div 
                key={`r2-${item.id}-${index}`} 
                className="testimonial-marquee-card"
                onClick={() => setExpandedImage(item)}
              >
                <img 
                  src={item.image} 
                  alt={`Client testimonial ${item.id}`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        {expandedImage && (
          <div className="testimonial-modal" onClick={() => setExpandedImage(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setExpandedImage(null)}>
                <XIcon className="w-6 h-6" />
              </button>
              <img src={expandedImage.image} alt={`WhatsApp client success screenshot - BulkJobApply testimonial`} className="modal-image" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Results/Wall of Proof Section
const ResultsSection = () => {
  const [ref, isVisible] = useScrollReveal();
  const [selectedMonth, setSelectedMonth] = useState(monthlyResults.length - 1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  
  const currentMonthData = monthlyResults[selectedMonth];
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const buildImageUrl = (pdfUrl, page) => {
    return `${backendUrl}/api/pdf-page-image?url=${encodeURIComponent(pdfUrl)}&page=${page}&scale=1.2&quality=75`;
  };

  useEffect(() => {
    if (!isVisible) return; // defer until section scrolls into view (ad-page speed)
    const fetchPdfInfo = async () => {
      if (!currentMonthData.pdfUrl) return;
      try {
        const response = await fetch(
          `${backendUrl}/api/pdf-info?url=${encodeURIComponent(currentMonthData.pdfUrl)}`
        );
        const data = await response.json();
        setTotalPages(data.page_count);
      } catch (error) {
        console.error('Failed to fetch PDF info:', error);
        setTotalPages(currentMonthData.pages || 50);
      }
    };
    fetchPdfInfo();
  }, [isVisible, selectedMonth, currentMonthData.pdfUrl, backendUrl, currentMonthData.pages]);

  useEffect(() => {
    if (!isVisible || !currentMonthData.pdfUrl) return;
    setIsLoading(true);
    setLoadError(false);
    setImageUrl(buildImageUrl(currentMonthData.pdfUrl, currentPage));
  }, [isVisible, currentPage, selectedMonth, currentMonthData.pdfUrl, backendUrl]);

  const retryImage = () => {
    setLoadError(false);
    setIsLoading(true);
    // cache-buster forces a fresh attempt
    setImageUrl(`${buildImageUrl(currentMonthData.pdfUrl, currentPage)}&r=${Date.now()}`);
  };

  useEffect(() => {
    if (!isVisible || !currentMonthData.pdfUrl || !totalPages) return;
    const pagesToPreload = [];
    if (currentPage > 1) pagesToPreload.push(currentPage - 1);
    if (currentPage < totalPages) pagesToPreload.push(currentPage + 1);
    if (currentPage + 2 <= totalPages) pagesToPreload.push(currentPage + 2);
    pagesToPreload.forEach(page => {
      const img = new Image();
      img.src = buildImageUrl(currentMonthData.pdfUrl, page);
    });
  }, [isVisible, currentPage, totalPages, currentMonthData.pdfUrl, backendUrl]);

  const handleMonthChange = (index) => {
    setSelectedMonth(index);
    setCurrentPage(1);
    setIsLoading(true);
  };

  return (
    <section className="results-section" id="results" ref={ref} aria-label="Monthly client results">
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">Wall of Proof</span>
        <h2 className="section-headline">Real Results Every Month</h2>
        <p className="section-subtitle">
          Browse through {totalPages || '...'} pages of client success screenshots from {currentMonthData.month} {currentMonthData.year}
        </p>

        <div className="results-month-picker" data-testid="results-month-tabs">
          <label className="results-month-label" htmlFor="results-month-select">Pick a month:</label>
          <select
            id="results-month-select"
            className="results-month-select"
            value={selectedMonth}
            onChange={(e) => handleMonthChange(Number(e.target.value))}
            data-testid="results-month-select"
          >
            {monthlyResults.map((month, index) => (
              <option key={month.id} value={index} data-testid={`month-tab-${month.id}`}>
                {month.month} {month.year} · {month.pages} pages
              </option>
            ))}
          </select>
        </div>

        <div className="results-image-viewer">
          <button 
            className="viewer-nav-btn prev"
            onClick={() => currentPage > 1 && setCurrentPage(p => p - 1)}
            disabled={currentPage <= 1 || isLoading}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <div className="image-viewer-main">
            <div className="viewer-header">
              <div className="viewer-title">
                <FileImage className="w-5 h-5" />
                <span>{currentMonthData.month} {currentMonthData.year} Results</span>
              </div>
              <div className="viewer-page-info">
                Page <span className="current-page">{currentPage}</span> of <span>{totalPages || '...'}</span>
              </div>
            </div>

            <div className="image-viewer-content">
              {isLoading && (
                <div className="image-loading-overlay">
                  <Loader2 className="w-10 h-10 animate-spin" />
                  <span>Loading page {currentPage}...</span>
                </div>
              )}
              {loadError && !isLoading && (
                <div className="image-error-overlay" data-testid="results-image-error">
                  <span>Couldn't load this page.</span>
                  <button type="button" className="page-nav-btn" onClick={retryImage} data-testid="results-image-retry">
                    Retry
                  </button>
                </div>
              )}
              {imageUrl && (
                <img 
                  src={imageUrl} 
                  alt={`${currentMonthData.month} ${currentMonthData.year} - Page ${currentPage}`}
                  className="pdf-page-image"
                  onLoad={() => { setIsLoading(false); setLoadError(false); }}
                  onError={() => { setIsLoading(false); setLoadError(true); }}
                />
              )}
            </div>

            <div className="viewer-progress">
              <div className="progress-fill" style={{ width: `${totalPages ? (currentPage / totalPages) * 100 : 0}%` }}></div>
            </div>

            <div className="viewer-footer">
              <div className="page-nav-buttons">
                <button className="page-nav-btn" onClick={() => setCurrentPage(1)} disabled={currentPage <= 1}>First</button>
                <button className="page-nav-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>Previous</button>
                <button className="page-nav-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
                <button className="page-nav-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>Last</button>
              </div>
              <div className="page-jump">
                <span>Go to:</span>
                <input 
                  type="number" min="1" max={totalPages || 1} value={currentPage}
                  onChange={(e) => { const val = parseInt(e.target.value); if (val >= 1 && val <= totalPages) setCurrentPage(val); }}
                  className="page-input"
                />
              </div>
            </div>
          </div>

          <button 
            className="viewer-nav-btn next"
            onClick={() => currentPage < totalPages && setCurrentPage(p => p + 1)}
            disabled={currentPage >= totalPages || isLoading}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Founder Section
const FounderSection = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section className="founder-section" ref={ref}>
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <div className="founder-container">
          <div className="founder-image-container">
            <img 
              src="/founder.webp" 
              alt="Shubham Maheshwari - Founder of BulkJobApply, Ex Lead Trader at Inspire International Trading DMCC Dubai" 
              className="founder-image"
              loading="lazy"
            />
          </div>
          <div className="founder-content">
            <h3 className="founder-greeting">Dear Fellow Job Seeker,</h3>
            <div className="founder-letter">
              <p>
                I know exactly what you're going through. The endless applications, the silence that follows, the frustration of not hearing back. I've been there myself, and I've watched countless talented professionals struggle with the same broken system.
              </p>
              <p>
                After years of working in corporate roles across Dubai, I realized something important: the job market rewards volume, but most people simply don't have the time to apply at scale while maintaining quality. That's why I built BulkJobApply.
              </p>
              <p>
                We're not another job board or AI tool that sprays generic applications. We're a team of real humans who understand what recruiters look for. We craft tailored applications, reach out directly to hiring managers, and handle the grind so you can focus on preparing for interviews.
              </p>
              <p>
                If you're tired of the job search treadmill, let's talk. I personally review every strategy call to make sure we can actually help before you commit.
              </p>
            </div>
            <div className="founder-signature">
              <div className="founder-name">Shubham Maheshwari</div>
              <div className="founder-title">
                Founder, BulkJobApply<br/>
                Ex Lead Trader at Inspire International Trading DMCC, Dubai<br/>
                Ex Business Excellence Engineer at Shobha Construction, Dubai
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section className="pricing-section" id="pricing" ref={ref} aria-label="Pricing plans">
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">Pricing</span>
        <h2 className="section-headline">
          Pick the plan that fits where you are.
        </h2>
        <p className="section-subtitle">
          Whether you're testing the water or all-in, every plan gets you in front of real hiring managers.
        </p>

        <div className="pricing-grid-4" data-testid="pricing-grid">
          {pricingTiers.map((tier, index) => {
            const isPremium = tier.id === 4; // Guaranteed tier
            return (
            <div 
              key={tier.id} 
              className={`pricing-card ${tier.popular ? 'popular' : ''} ${isPremium ? 'premium' : ''} ${isVisible ? 'animate-stagger' : ''}`}
              style={{ animationDelay: `${index * 120}ms` }}
              data-testid={`pricing-card-${tier.id}`}
            >
              {tier.badge && (
                <div className={`tier-badge ${tier.popular ? 'popular-badge' : 'urgency-badge'}`}>
                  {tier.badge}
                </div>
              )}
              
              <h3 className="tier-name">{tier.name}</h3>
              {tier.duration && (
                <span className="tier-duration">{tier.duration}</span>
              )}
              
              <div className="tier-price">
                <span className="price-amount">{tier.price}</span>
                <span className="price-period">{tier.period}</span>
              </div>

              {tier.splitPayment && (
                <SplitPaymentTimeline variant={isPremium ? 'dark' : 'light'} />
              )}

              {tier.description && (
                <p className="tier-description" data-testid={`tier-description-${tier.id}`}>
                  {tier.description}
                </p>
              )}
              
              <p className="tier-applications">{tier.applications}</p>
              
              <ul className="tier-features">
                {tier.features.map((feature, i) => (
                  <li key={i} className="feature-item">
                    <Check className="feature-check" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {tier.bestFor && (
                <div className="tier-best-for">
                  <span className="best-for-label">Best for:</span> {tier.bestFor}
                </div>
              )}

              {tier.upsell && (
                <div className="tier-upsell">
                  {tier.upsell}
                </div>
              )}
              
              <div className="tier-guarantee">
                <Shield className="guarantee-icon" />
                <span>{tier.guarantee}</span>
              </div>
              
            <Button
              type="button"
              onClick={() => scrollToLeadForm(`pricing_tier_${tier.id}`)}
              className={`tier-cta ${tier.popular ? 'cta-popular' : ''}`}
              style={{ width: '100%', marginTop: 'auto' }}
              data-testid={`pricing-cta-${tier.id}`}
            >
              Reserve My Seat
              <ArrowRight className="w-4 h-4" />
            </Button>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section className="faq-section" id="faq" ref={ref} aria-label="Frequently asked questions">
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <span className="section-label">FAQ</span>
        <h2 className="section-headline">Questions? Answered.</h2>

        <div className="faq-container">
          <Accordion type="single" collapsible className="faq-accordion">
            {faqItems.map((item) => (
              <AccordionItem key={item.id} value={`item-${item.id}`} className="faq-item" data-testid={`faq-item-${item.id}`}>
                <AccordionTrigger className="faq-trigger" data-testid={`faq-question-${item.id}`}>
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="faq-content" data-testid={`faq-answer-${item.id}`}>
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section, repeats the inline lead form for scrollers
const FinalCTASection = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section className="final-cta-section" ref={ref} id="get-started">
      <div className={`section-content ${isVisible ? 'animate-fade-up' : ''}`}>
        <div className="final-cta-grid">
          <div className="final-cta-copy">
            <h2 className="cta-headline">
              Stop Applying. <br />
              Start Interviewing.
            </h2>

            <p className="cta-subtext">
              Cut 2 months off your job search. <strong>Get your weekends back and your calendar full of interviews.</strong>
            </p>

            <ul className="final-cta-list">
              <li><span className="final-cta-check">✓</span> 780+ tailored applications every month</li>
              <li><span className="final-cta-check">✓</span> Every hiring manager triple-touched: application + LinkedIn DM + cold email</li>
              <li><span className="final-cta-check">✓</span> 10+ interviews in 90 days, guaranteed minimum</li>
            </ul>
          </div>

          <div className="final-cta-form">
            <InlineLeadForm
              source="final_cta"
              variant="compact"
              headline="Get your free plan. Reply in 15 min."
              subheadline="Enter your details and our team messages you within 15 minutes with your job-search plan. Free, no card, no sales call."
              ctaText="Get My Free Plan"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer. Meta Ads optimized: brand only, zero exit links
const Footer = () => {
  return (
    <footer className="footer" data-testid="site-footer">
      <div className="footer-content">
        <div className="footer-main">
          <div className="footer-brand">
            <div className="footer-logo">
              <img src="/logo.webp" alt="BulkJobApply" className="footer-logo-image" width="120" height="25" />
            </div>
            <p className="footer-tagline">
              Your personal job team applies to 780+ roles a month and triple-touches every hiring manager. 10 interviews guaranteed in 90 days.
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <span className="footer-copyright">&copy; {new Date().getFullYear()} BulkJobApply. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  const [showSticky, setShowSticky] = useState(false);
  const [heroFormVisible, setHeroFormVisible] = useState(true);

  useEffect(() => {
    captureAdTracking(); // persist UTM/fbclid from the ad click URL
    const handleScroll = () => {
      setShowSticky(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide the sticky bottom CTA while the hero lead form is on screen so the
  // two CTAs never overlap/duplicate visually on mobile.
  useEffect(() => {
    const heroForm = document.querySelector('[data-testid="inline-lead-form-hero"]');
    if (!heroForm || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setHeroFormVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(heroForm);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page">
      <UrgencyBar />
      <Header showSticky={showSticky} />
      <HeroSection />
      <GuaranteeStrip />
      <LogosSection />
      <ResultsSection />
      <TripleTouchSection />
      <TestimonialsSection />
      <SolutionHowItWorksSection />
      <ComparisonSection />
      <FounderSection />
      <FinalCTASection />
      <FAQSection />
      <Footer />

      {/* Mobile sticky bottom CTA, scrolls to hero form */}
      {showSticky && !heroFormVisible && (
        <button
          type="button"
          className="sticky-mobile-cta"
          onClick={() => scrollToLeadForm('sticky_mobile')}
          data-testid="sticky-mobile-cta"
        >
          Get My Free Plan
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default LandingPage;
